import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { submitContactForm } from '@/lib/api/forms';
import { FormShell, type FormStatus } from './FormShell';
import { SmsConsentDisclosure } from './SmsConsentDisclosure';
import { Honeypot } from './Honeypot';
import { useHoneypot } from './use-honeypot';

const SERVICE_TYPES = ['Residential', 'Commercial', 'Medical', 'Other'];

export function FormPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: 'Residential',
    description: '',
  });
  const [smsConsent, setSmsConsent] = useState(false);
  const [status, setStatus] = useState<FormStatus>('idle');
  const { value: hp, honeypotProps } = useHoneypot();

  useEffect(() => {
    const handleOpen = () => setIsVisible(true);
    window.addEventListener('openContactForm', handleOpen);
    return () => window.removeEventListener('openContactForm', handleOpen);
  }, []);

  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isVisible]);

  const handleClose = () => {
    setIsVisible(false);
    if (status === 'sent') {
      setStatus('idle');
      setForm({ name: '', email: '', phone: '', serviceType: 'Residential', description: '' });
      setSmsConsent(false);
    }
  };

  const onChange =
    (key: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');

    const message = [`Service Type: ${form.serviceType}`, `Description: ${form.description}`].join('\n');

    try {
      await submitContactForm(
        {
          name: form.name,
          email: form.email,
          phone: form.phone,
          message,
        },
        'quote_request',
        smsConsent,
        hp,
      );
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  if (!isVisible) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 animate-fadeIn"
        onClick={handleClose}
      />

      <div
        onClick={handleClose}
        className="fixed inset-0 z-50 overflow-y-auto overscroll-contain flex items-start justify-center p-4 pb-[calc(2rem+env(safe-area-inset-bottom))]"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-lg my-4 sm:my-8 animate-slideUp"
        >
          <button
            onClick={handleClose}
            className="absolute -top-3 -right-3 z-10 w-10 h-10 bg-charcoal-deep border border-thunder/40 flex items-center justify-center text-thunder hover:bg-thunder hover:text-charcoal-deep transition"
            aria-label="Close form"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="mb-6 px-2">
            <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-wide mb-2">
              GET YOUR <span className="text-thunder">FREE QUOTE</span>
            </h2>
            <p className="text-white/60 text-sm">
              Tell us about your space. We'll reply within one business hour.
            </p>
          </div>

          <FormShell
            eyebrow="// REQUEST QUOTE"
            status={status}
            successTitle="QUOTE REQUEST RECEIVED"
            successBody="A team member will reach out within one business hour."
          >
            <form onSubmit={onSubmit} className="space-y-6">
              <Honeypot {...honeypotProps} id="popup_company_website" />
              <div className="grid sm:grid-cols-2 gap-6">
                <input
                  className="bold-input"
                  placeholder="FULL NAME"
                  required
                  value={form.name}
                  onChange={onChange('name')}
                />
                <input
                  className="bold-input"
                  type="tel"
                  placeholder="PHONE"
                  required
                  value={form.phone}
                  onChange={onChange('phone')}
                />
              </div>

              <input
                className="bold-input"
                type="email"
                placeholder="EMAIL"
                required
                value={form.email}
                onChange={onChange('email')}
              />

              <div>
                <label className="font-display text-electric tracking-[0.3em] text-xs block mb-3">
                  SERVICE TYPE
                </label>
                <select
                  className="bold-input"
                  value={form.serviceType}
                  onChange={onChange('serviceType')}
                >
                  {SERVICE_TYPES.map((t) => (
                    <option key={t} className="bg-charcoal-deep">
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <textarea
                className="bold-input min-h-[110px] resize-none"
                placeholder="BRIEF DESCRIPTION (SPACE SIZE, SPECIAL NEEDS, TIMING)"
                required
                maxLength={500}
                value={form.description}
                onChange={onChange('description')}
              />

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-thunder cursor-pointer flex-shrink-0"
                  aria-label="SMS consent"
                />
                <SmsConsentDisclosure />
              </label>

              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full pulse-btn bg-thunder hover:bg-thunder-dark disabled:opacity-60 text-charcoal-deep font-display font-bold py-5 tracking-[0.3em] text-sm transition"
              >
                {status === 'sending' ? 'SENDING…' : 'REQUEST FREE QUOTE →'}
              </button>
            </form>
          </FormShell>
        </div>
      </div>
    </>
  );
}
